import React from "react";
import { cn } from "../utils/cn";

interface AssumptionsDisplayProps {
  /** Expected return rate as decimal (e.g. 0.06 for 6%) */
  expectedReturnRate: number;
  /** Inflation rate as decimal (e.g. 0.021 for 2.1%) */
  inflationRate: number;
  /** Real estate appreciation rate as decimal (e.g. 0.035 for 3.5%) */
  realEstateAppreciationRate: number;
  /** Expense growth rate as decimal (e.g. 0.02 for 2%) */
  expenseGrowthRate: number;
  /** Called when user clicks Edit — passes settings category */
  onEdit?: (category: string) => void;
}

interface AssumptionCardData {
  label: string;
  value: string;
  description: string;
}

function formatRate(rate: number): string {
  const pct = rate * 100;
  // Remove trailing zeros after decimal
  const formatted = pct.toFixed(2).replace(/\.?0+$/, "");
  return `${formatted}%`;
}

export const AssumptionsDisplay: React.FC<AssumptionsDisplayProps> = React.memo(
  ({
    expectedReturnRate,
    inflationRate,
    realEstateAppreciationRate,
    expenseGrowthRate,
    onEdit,
  }) => {
    const cards: AssumptionCardData[] = [
      {
        label: "Expected Return Rate",
        value: formatRate(expectedReturnRate),
        description: "Annual growth rate for your investment portfolio",
      },
      {
        label: "Inflation Rate",
        value: formatRate(inflationRate),
        description:
          "How much prices increase each year, reducing purchasing power",
      },
      {
        label: "Real Estate Appreciation",
        value: formatRate(realEstateAppreciationRate),
        description: "Annual growth rate for your property values",
      },
      {
        label: "Expense Growth",
        value: formatRate(expenseGrowthRate),
        description: "How your spending changes in retirement",
      },
    ];

    return (
      <div className="assumptions-display">
        <h3 className="assumptions-title">Assumptions Applied</h3>

        {/* Compact inline summary for at-a-glance visibility (Gap 6.1.3) */}
        <div className="assumptions-compact-summary">
          <span className="assumptions-compact-item">
            <span className="assumptions-compact-label">Return</span>
            <span className="assumptions-compact-value">
              {formatRate(expectedReturnRate)}
            </span>
          </span>
          <span className="assumptions-compact-sep" aria-hidden="true">
            ·
          </span>
          <span className="assumptions-compact-item">
            <span className="assumptions-compact-label">Inflation</span>
            <span className="assumptions-compact-value">
              {formatRate(inflationRate)}
            </span>
          </span>
          <span className="assumptions-compact-sep" aria-hidden="true">
            ·
          </span>
          <span className="assumptions-compact-item">
            <span className="assumptions-compact-label">Real Estate</span>
            <span className="assumptions-compact-value">
              {formatRate(realEstateAppreciationRate)}
            </span>
          </span>
          <span className="assumptions-compact-sep" aria-hidden="true">
            ·
          </span>
          <span className="assumptions-compact-item">
            <span className="assumptions-compact-label">Expenses</span>
            <span className="assumptions-compact-value">
              {formatRate(expenseGrowthRate)}
            </span>
          </span>
        </div>

        <div className="assumptions-grid">
          {cards.map((card) => (
            <div key={card.label} className="assumptions-card">
              <span className="assumptions-card-label">{card.label}</span>
              <span className="assumptions-card-value">{card.value}</span>
              <p className="assumptions-card-description">{card.description}</p>
              <button
                type="button"
                className={cn("assumptions-card-edit")}
                onClick={() => onEdit?.("assumptions")}
              >
                Edit →
              </button>
            </div>
          ))}
        </div>

        <div className="assumptions-info-note">
          <span aria-hidden="true">💡</span>
          <span>
            Changes flow through instantly — your projections update in real
            time.
          </span>
        </div>
      </div>
    );
  }
);

AssumptionsDisplay.displayName = "AssumptionsDisplay";
